import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import { fetchTools } from "../../../api/tools";
import { PMDialog } from "../../utils/PMDialog";

const EMPLOYEE_TOOL_FIELDS =
  "id,title,tool_number,description,status,active,category,brand,model,serial_number,assigned_user_id,assigned_user,next_service_date,service_overdue,service_due_soon,assignment_periods";

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const currentAssignment = (tool, userId) => {
  const periods = Array.isArray(tool?.assignment_periods)
    ? tool.assignment_periods
    : [];
  return (
    periods.find(
      (period) =>
        period?.status === "active" &&
        String(period?.user_id) === String(userId)
    ) || null
  );
};

const toolStatusClass = (tool) => {
  if (tool?.service_overdue) return "bg-red-50 text-red-700 border-red-200";
  if (tool?.service_due_soon)
    return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-emerald-50 text-emerald-700 border-emerald-200";
};

export const EmployeeToolsDialog = ({ open, onClose, userId }) => {
  const { t } = useTranslation();
  const [state, setState] = useState({
    isLoading: false,
    data: [],
    error: null,
  });

  useEffect(() => {
    if (!open || !userId) return;

    let mounted = true;
    setState({ isLoading: true, data: [], error: null });

    fetchTools({
      assigned_user_id: userId,
      assignment: "assigned",
      per_page: 100,
      fields: EMPLOYEE_TOOL_FIELDS,
    })
      .then((response) => {
        if (!mounted) return;
        setState({
          isLoading: false,
          data: Array.isArray(response?.data) ? response.data : [],
          error: null,
        });
      })
      .catch(() => {
        if (!mounted) return;
        setState({
          isLoading: false,
          data: [],
          error: t("tool.employee_tools_load_error"),
        });
      });

    return () => {
      mounted = false;
    };
  }, [open, t, userId]);

  const tools = useMemo(
    () =>
      (state.data || []).filter(
        (tool) => String(tool?.assigned_user_id) === String(userId)
      ),
    [state.data, userId]
  );

  return (
    <PMDialog open={open} onClose={onClose} title="tool.employee_tools_title" size="4xl">
      <div className="space-y-5">
        <div className="rounded-2xl bg-slate-50 px-4 py-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white text-slate-700 shadow-sm">
              <WrenchScrewdriverIcon className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900">
                {t("tool.employee_tools_intro_title")}
              </div>
              <p className="mt-1 text-sm text-slate-600">
                {t("tool.employee_tools_intro")}
              </p>
            </div>
          </div>
        </div>

        {state.error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700">
            {state.error}
          </div>
        ) : null}

        {state.isLoading ? (
          <div className="text-sm text-slate-500">{t("tool.employee_tools_loading")}</div>
        ) : tools.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center">
            <CubeIcon className="mx-auto h-10 w-10 text-slate-400" />
            <h3 className="mt-3 text-sm font-semibold text-slate-900">
              {t("tool.employee_tools_empty_title")}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              {t("tool.employee_tools_empty")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {tools.map((tool) => {
              const assignment = currentAssignment(tool, userId);
              const assignedOn = formatDate(assignment?.assigned_on);
              const nextService = formatDate(tool?.next_service_date);

              return (
                <article
                  key={tool.id}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        #{tool.tool_number || tool.id}
                      </div>
                      <h3 className="mt-1 truncate text-base font-semibold text-slate-950">
                        {tool.title}
                      </h3>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {tool.category?.name ? (
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
                            {tool.category.name}
                          </span>
                        ) : null}
                        {tool.brand?.name ? (
                          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-600">
                            {tool.brand.name}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <span
                      className={`inline-flex flex-shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${toolStatusClass(
                        tool
                      )}`}
                    >
                      {tool?.service_overdue || tool?.service_due_soon ? (
                        <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                      ) : (
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                      )}
                      {tool.status ? t(`tool.status.${tool.status}`) : t("tool.status")}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                    <div className="rounded-xl bg-slate-50 px-3 py-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        {t("tool.employee_tools_assigned_on")}
                      </div>
                      <div className="mt-1 font-semibold text-slate-900">
                        {assignedOn || t("tool.employee_tools_unknown_date")}
                      </div>
                    </div>
                    <div className="rounded-xl bg-slate-50 px-3 py-3">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        {t("tool.next_service")}
                      </div>
                      <div className="mt-1 font-semibold text-slate-900">
                        {nextService || t("tool.employee_tools_no_service")}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 space-y-1 text-sm text-slate-600">
                    {tool.model ? <div>{t("tool.model")}: {tool.model}</div> : null}
                    {tool.serial_number ? (
                      <div>{t("tool.serial_number")}: {tool.serial_number}</div>
                    ) : null}
                    {tool.description ? <div>{tool.description}</div> : null}
                  </div>

                  {assignment?.days_held ? (
                    <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700">
                      <CalendarDaysIcon className="h-4 w-4" />
                      {t("tool.employee_tools_days_held", {
                        count: assignment.days_held,
                      })}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </div>
    </PMDialog>
  );
};
